import React from "react";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import MenuOpenRoundedIcon from "@mui/icons-material/MenuOpenRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { NavLink } from "react-router-dom";

import UserAvatarIcon from "../UserAvatarIcon/UserAvatarIcon";
import "./styles.css";

export interface AppHeaderMenuItem {
  route: string;
  label: string;
  disabled?: boolean;
  action?: "logout";
}

interface AppHeaderProps {
  isLightMode: boolean;
  isSidebarOpen: boolean;
  isMobileMenuOpen?: boolean;
  isSticky?: boolean;
  logo: string;
  searchValue: string;
  onMenuClick: () => void;
  onMobileMenuClick?: () => void;
  onSearchChange: (value: string) => void;
  onSearchSubmit?: () => void;
  onThemeToggle: () => void;
  avatarUrl?: string;
  menuItems?: AppHeaderMenuItem[];
  profileMenuItems?: AppHeaderMenuItem[];
  showAdminAccess?: boolean;
  onAdminAccessClick?: () => void;
  onLogout?: () => void;
}

const AppHeader = ({
  isLightMode,
  isSidebarOpen,
  isMobileMenuOpen = false,
  isSticky = true,
  logo,
  searchValue,
  onMenuClick,
  onMobileMenuClick,
  onSearchChange,
  onSearchSubmit,
  onThemeToggle,
  avatarUrl,
  menuItems = [],
  profileMenuItems = [],
  showAdminAccess = false,
  onAdminAccessClick,
  onLogout,
}: AppHeaderProps) => {
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const menuWrapperRef = React.useRef<HTMLDivElement>(null);
  const profileMenuWrapperRef = React.useRef<HTMLDivElement>(null);
  const searchHintId = `app-header-search-hint-${React.useId()}`;
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = React.useState(false);

  React.useEffect(() => {
    if (!isMenuOpen && !isProfileMenuOpen) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (menuWrapperRef.current?.contains(event.target as Node)) return;
      if (profileMenuWrapperRef.current?.contains(event.target as Node)) return;

      setIsMenuOpen(false);
      setIsProfileMenuOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideClick);

    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [isMenuOpen, isProfileMenuOpen]);

  const renderMenuItems = (items: AppHeaderMenuItem[]) =>
    items.map((menuItem) =>
      menuItem.disabled ? (
        <span
          key={menuItem.route}
          className="app-header__menu-item app-header__menu-item--disabled"
          role="menuitem"
          aria-disabled="true"
        >
          {menuItem.label}
        </span>
      ) : menuItem.action === "logout" ? (
        <button
          key={menuItem.route}
          type="button"
          className="app-header__menu-item app-header__menu-item--danger"
          role="menuitem"
          onClick={() => {
            setIsMenuOpen(false);
            setIsProfileMenuOpen(false);
            onLogout?.();
          }}
        >
          {menuItem.label}
        </button>
      ) : (
        <NavLink
          key={menuItem.route}
          to={menuItem.route}
          className="app-header__menu-item"
          role="menuitem"
          onClick={() => {
            setIsMenuOpen(false);
            setIsProfileMenuOpen(false);
          }}
        >
          {menuItem.label}
        </NavLink>
      )
    );

  const handleClearSearch = () => {
    onSearchChange("");
    searchInputRef.current?.focus();
  };

  return (
    <header
      className={`app-header${isSticky ? "" : " app-header--static"}`}
    >
      <NavLink
        to="/app"
        className="app-header__mobile-brand"
        aria-label="Ir para a tela inicial"
      >
        <img src={logo} alt="Conectado" />
      </NavLink>

      <div className="app-header__leading">
        <button
          type="button"
          className={`app-header__collapse-button${
            isSidebarOpen ? "" : " app-header__collapse-button--collapsed"
          }`}
          onClick={onMenuClick}
          aria-label={isSidebarOpen ? "Retrair menu lateral" : "Expandir menu lateral"}
          aria-controls="app-sidebar"
          aria-expanded={isSidebarOpen}
        >
          <MenuOpenRoundedIcon aria-hidden="true" />
        </button>

        {showAdminAccess && (
          <button
            type="button"
            className="app-header__admin-button"
            onClick={onAdminAccessClick}
          >
            <AdminPanelSettingsOutlinedIcon aria-hidden="true" />
            <span>Acessar Painel Administrativo</span>
          </button>
        )}
      </div>

      <form
        className="app-header__search"
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          onSearchSubmit?.();
        }}
      >
        <button
          type="submit"
          className="app-header__search-submit"
          aria-label="Buscar"
        >
          <SearchRoundedIcon aria-hidden="true" />
        </button>
        <input
          ref={searchInputRef}
          type="search"
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Pesquise e pressione Enter"
          aria-label="Pesquisar"
          aria-describedby={searchHintId}
        />
        <span id={searchHintId} className="app-header__search-hint">
          Pressione Enter para ver os resultados da pesquisa.
        </span>
        {searchValue && (
          <button
            type="button"
            className="app-header__search-clear"
            onClick={handleClearSearch}
            aria-label="Limpar pesquisa"
          >
            <CloseRoundedIcon aria-hidden="true" />
          </button>
        )}
      </form>

      <div className="app-header__actions">
        <button
          type="button"
          className={`app-header__theme-toggle${
            isLightMode ? "" : " app-header__theme-toggle--dark"
          }`}
          onClick={onThemeToggle}
          aria-label={isLightMode ? "Ativar modo escuro" : "Ativar modo claro"}
          aria-pressed={!isLightMode}
        >
          <span>{isLightMode ? "LIGHT MODE" : "DARK MODE"}</span>
          <span className="app-header__theme-icon" aria-hidden="true">
            {isLightMode ? (
              <LightModeOutlinedIcon />
            ) : (
              <DarkModeOutlinedIcon />
            )}
          </span>
        </button>

        <div
          className="app-header__menu-wrapper"
          ref={menuWrapperRef}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setIsMenuOpen(false);
              setIsProfileMenuOpen(false);
            }
          }}
        >
          <button
            type="button"
            className="app-header__menu-button app-header__menu-button--desktop"
            onClick={() => {
              setIsProfileMenuOpen(false);
              setIsMenuOpen((current) => !current);
            }}
            aria-label="Menu"
            aria-haspopup="true"
            aria-controls="app-header-menu"
            aria-expanded={isMenuOpen}
          >
            <MenuRoundedIcon />
          </button>

          {isMenuOpen && (
            <div id="app-header-menu" className="app-header__menu" role="menu">
              {renderMenuItems(menuItems)}
            </div>
          )}
        </div>

        <button
          type="button"
          className="app-header__menu-button app-header__menu-button--mobile"
          onClick={onMobileMenuClick}
          aria-label={isMobileMenuOpen ? "Fechar menu" : "Abrir menu"}
          aria-controls="app-sidebar"
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? (
            <CloseRoundedIcon aria-hidden="true" />
          ) : (
            <MenuRoundedIcon aria-hidden="true" />
          )}
        </button>

        <div
          className="app-header__profile-wrapper"
          ref={profileMenuWrapperRef}
          onKeyDown={(event) => {
            if (event.key === "Escape") setIsProfileMenuOpen(false);
          }}
        >
          <button
            type="button"
            className="app-header__avatar"
            onClick={() => {
              setIsMenuOpen(false);
              setIsProfileMenuOpen((current) => !current);
            }}
            aria-label="Perfil do usuário"
            aria-haspopup="true"
            aria-controls="app-header-profile-menu"
            aria-expanded={isProfileMenuOpen}
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="" />
            ) : (
              <UserAvatarIcon />
            )}
          </button>

          {isProfileMenuOpen && (
            <div
              id="app-header-profile-menu"
              className="app-header__menu app-header__profile-menu"
              role="menu"
            >
              {renderMenuItems(profileMenuItems)}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
